import { ServiceFormats, ServiceTypes } from '@types';
import type { IOrderStore } from '@sub/stores/order';
import type { Ref } from 'vue';
import { durationDifference, formatHM } from '@sub/utils/times';
import { findWeekdayNum, getWeekdaysBetween } from '@utils/priceLibrary/priceWeeks';
import { useServicePriceSettings } from '@utils/priceLibrary/servicePriceSettings';
import { getServiceMinTimeLength } from '@utils/priceLibrary/service';
import { getSeatsData } from '@utils/priceLibrary/additionalSeats';
import type { SlotAvailability } from '@utils/calendar/Disabled';

export function usePriceButton(order: Ref<IOrderStore>, slotAvailability: Ref<SlotAvailability | undefined>) {
  const { formatPrice } = usePriceFormatter();
  const i18n = useI18n();

  const seatsData = computed(() => getSeatsData(order.value.service));

  const seats = computed(() => {
    if (order.value.service?.type !== ServiceTypes.GroupService) return false;

    const min = seatsData.value.min || 0
    let max = seatsData.value.max || 1

    if (order.value?.timeslot) {
      max = order.value.timeslot.availableSpots
    }

    if (slotAvailability?.value) {
      max = slotAvailability.value.free;
    }

    return {
      min: Math.min(min, max),
      max
    }
  });

  const servicePriceSettings = computed(() => useServicePriceSettings(order.value.service));

  function getDataFromPriceLib() {
    if (!order.value.dates?.from || !servicePriceSettings) return
    const options = {
      dateStart: order.value.dates.from,
      dateEnd: order.value.dates.to,
      amountPeople: order.value.service?.type !== ServiceTypes.Individual ? undefined : +order.value.service?.amountPeopleMin,
      target: order.value.target
    }
    if (order.value.service?.type === ServiceTypes.Individual) {
      options.metaPriority = false
    }
    return {
      getParamPrice: () => servicePriceSettings.value.getParamPrice(options),
      getSettings: () => servicePriceSettings.value.getSettings(options).map(({ setting }) => {
        const weekdaysBetween = getWeekdaysBetween(options.dateStart, options.dateEnd);
        return {
          ...setting,
          parameters: setting.parameters.filter(p => p.weekDays.map(findWeekdayNum).some(w => weekdaysBetween.includes(w)))
        };
      })
    }
  }

  const price = computed(() => getDataFromPriceLib()?.getParamPrice().calcParamPrice());
  const isWithout = computed(() => order.value.service?.format === ServiceFormats.without || (price.value?.general || 0) <= 0);

  const minTimeLength = computed(() => getServiceMinTimeLength(order.value.service, getDataFromPriceLib()?.getSettings(), {
    dateStart: order.value.dates.from,
    dateEnd: order.value.dates.to,
  }) || 0);

  const minTimeLengthText = computed(() => minTimeLength.value ? formatHM(minTimeLength.value, i18n) : minTimeLength.value);

  const minLengthError = computed(() => order.value.dates ? durationDifference(order.value.dates.to, +order.value.dates.from) < minTimeLength.value : false);
  const text = computed(() => isWithout.value
    ? i18n.t('page.booking.service.info.button.booking.text.base')
    : i18n.t('page.booking.service.info.button.booking.text.from', {
      price: formatPrice(price.value?.general || 0)
    }));

  return {
    seats,
    price,
    isWithout,
    text,
    minLengthError,
    minTimeLengthText,
  };
}
